import { Alert, Form, Input, Modal, Select } from 'antd';
import { useCallback, useEffect } from 'react';
import { useRemoteSearchOptions } from '../../../shared/hooks/use-remote-search-options';
import { searchOverrideRequesters } from '../dispatch.api';

interface FormValues {
  requestedById: string;
  reason: string;
}

interface EligibilityOverrideModalProps {
  open: boolean;
  /** What is being overridden, in the dispatcher's words: "Truck KDX 123A: overdue for service". */
  subject: string;
  submitting: boolean;
  error: string | null;
  onSubmit: (values: FormValues) => Promise<boolean>;
  onCancel: () => void;
}

/** An approver records the override and names the dispatcher who asked for it: the two must be different people, and the server enforces it. */
export function EligibilityOverrideModal({ open, subject, submitting, error, onSubmit, onCancel }: EligibilityOverrideModalProps) {
  const [form] = Form.useForm<FormValues>();
  const fetchRequesters = useCallback(
    (q: string | undefined) =>
      searchOverrideRequesters(q).then((people) => people.map((person) => ({ value: person.id, label: `${person.name} (${person.email})` }))),
    [],
  );
  const { selectProps } = useRemoteSearchOptions(fetchRequesters);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  async function submit() {
    const values = await form.validateFields();
    if (await onSubmit(values)) form.resetFields();
  }

  return (
    <Modal open={open} title="Override this block" onOk={submit} onCancel={onCancel} confirmLoading={submitting} destroyOnClose okText="Record override">
      <p className="mb-4 text-muted">{subject}</p>
      <p className="mb-4 text-sm text-muted">
        The override covers this check on this trip only, whichever time window it was judged in. Other checks on the same truck, trailer or driver still apply.
      </p>
      {error && <Alert type="error" showIcon message={error} className="mb-4" />}
      <Form<FormValues> form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="requestedById" label="Requested by" rules={[{ required: true, message: 'Choose the dispatcher who asked' }]}>
          <Select {...selectProps} placeholder="Select or search dispatchers" notFoundContent={selectProps.loading ? 'Searching…' : 'No dispatchers found'} />
        </Form.Item>
        <Form.Item
          name="reason"
          label="Reason"
          rules={[
            { required: true, whitespace: true, message: 'Say why this block is being overridden' },
            { max: 500, message: 'At most 500 characters' },
          ]}
        >
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
